import React from "react";
import {Alert, Spinner} from "peggirkit";
import {FormattedMessage} from "react-intl";
import {DayReport} from "@/data/entities/dayReport";
import {SetStateFn} from "@/utils/SetStateUtils";
import DayReportStats from "@widgets/reports/dayReport/stats/DayReportStats";
import DayReportPayments from "@widgets/reports/dayReport/payments/DayReportPayments";
import DayReportVat from "@widgets/reports/dayReport/vat/DayReportVat";
import DayReportRefunds from "@widgets/reports/dayReport/refunds/DayReportRefunds";
import DayReportSummary from "@widgets/reports/dayReport/summary/DayReportSummary";
import EmployeesClockedNotification from "@widgets/reports/dayReport/stats/EmployeesClockedNotification";

type Props = {
    dayReport?: DayReport,
    isFetching: boolean,
    isError: boolean,
    setShowExportModal: SetStateFn<boolean>,
};

/**
 * The day report as a dashboard: key figures, payments, VAT, refunds and a
 * summary with the contents of the CSV export.
 */
const DayReportDashboard = ({dayReport, isFetching, isError, setShowExportModal}: Props) => {
    if (isError) {
        return (
            <Alert
                type={"danger"}
                title={<FormattedMessage id={"dayReport.loadFailed"}/>}
            />
        );
    }

    if (isFetching || dayReport === undefined) {
        return (
            <div className={"flex justify-center py-12"}>
                <Spinner size={"lg"}/>
            </div>
        );
    }

    return (
        <div className={"flex flex-col gap-6"}>
            {
                dayReport.openTimeEntryCount > 0 &&
                <EmployeesClockedNotification count={dayReport.openTimeEntryCount}/>
            }

            <DayReportStats dayReport={dayReport}/>

            <div className={"grid grid-cols-1 items-start gap-6 xl:grid-cols-3"}>
                <div className={"xl:col-span-2"}>
                    <DayReportPayments dayReport={dayReport}/>
                </div>

                <DayReportVat dayReport={dayReport}/>
            </div>

            <DayReportRefunds dayReport={dayReport}/>

            <DayReportSummary
                dayReport={dayReport}
                setShowExportModal={setShowExportModal}
            />
        </div>
    );
};

export default DayReportDashboard;